import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";

export default function Testimonials() {
  const { t } = useI18n();
  const [i, setI] = useState(0);
  const items = t.testimonials.items;

  const prev = () => setI((p) => (p - 1 + items.length) % items.length);
  const next = () => setI((p) => (p + 1) % items.length);

  const current = items[i];

  return (
    <section
      data-testid="testimonials-section"
      className="relative bg-black py-28 md:py-40 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-black to-zinc-950" />
      <div className="relative container mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="w-10 h-px bg-lux" />
          <span className="text-[11px] uppercase tracking-[0.3em] text-lux">
            {t.testimonials.label}
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-4xl mb-16 text-balance"
        >
          {t.testimonials.title}
        </motion.h2>

        <div className="relative max-w-5xl">
          <Quote size={80} className="text-white/5 absolute -top-6 -left-2" strokeWidth={1} />

          <AnimatePresence mode="wait">
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`testimonial-${i}`}
            >
              <p className="font-display font-extralight text-2xl sm:text-3xl lg:text-4xl leading-snug tracking-tight text-white text-balance">
                &ldquo;{current.q}&rdquo;
              </p>

              <div className="mt-12 flex items-center gap-5">
                <div className="w-12 h-12 rounded-full luxury-glass flex items-center justify-center font-display text-white/80">
                  {current.n.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                </div>
                <div>
                  <div className="font-display text-lg text-lux">{current.n}</div>
                  <div className="text-xs uppercase tracking-[0.22em] text-white/50">
                    {current.c}
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-16 flex items-center justify-between gap-6">
            <div className="flex gap-1">
              {items.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setI(idx)}
                  data-testid={`testimonial-dot-${idx}`}
                  aria-label={`Testimonial ${idx + 1}`}
                  className={`h-1 rounded-full transition-all duration-500 ${
                    idx === i ? "w-10 bg-white" : "w-4 bg-white/20"
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={prev}
                data-testid="testimonial-prev"
                aria-label="Previous"
                className="w-11 h-11 rounded-full border border-white/15 hover:border-white hover:bg-white hover:text-black transition-all flex items-center justify-center"
              >
                <ArrowLeft size={16} />
              </button>
              <button
                onClick={next}
                data-testid="testimonial-next"
                aria-label="Next"
                className="w-11 h-11 rounded-full border border-white/15 hover:border-white hover:bg-white hover:text-black transition-all flex items-center justify-center"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
